import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Clock, Tag, Star } from "lucide-react";
import { Recipe } from "@/types/recipe";
import { SimilarityMatch } from "@/utils/recipeSimilarity";
import { Link } from "react-router-dom";

interface SimilarRecipeCardProps {
  similarityMatch: SimilarityMatch;
  onSelect: (recipe: Recipe) => void;
  isCompact?: boolean;
}

export function SimilarRecipeCard({ similarityMatch, onSelect, isCompact = false }: SimilarRecipeCardProps) {
  const { recipe, score, reasons } = similarityMatch;
  
  const getScoreColor = (score: number) => {
    if (score >= 60) return "text-emerald-600 bg-emerald-50 border-emerald-200";
    if (score >= 40) return "text-yellow-600 bg-yellow-50 border-yellow-200";
    return "text-blue-600 bg-blue-50 border-blue-200";
  };

  const getScoreLabel = (score: number) => {
    if (score >= 60) return "High match";
    if (score >= 40) return "Good match";
    return "Similar";
  };

  return (
    <Card className="border-l-4 border-l-primary/30 hover:shadow-md transition-shadow">
      <CardContent className={isCompact ? "p-3" : "p-4"}>
        <div className="space-y-2">
          {/* Header with title and score */}
          <div className="flex items-start justify-between gap-2">
            <Link 
              to={`/recipe/${recipe.id}`} 
              className={`font-medium leading-tight hover:text-primary transition-colors ${isCompact ? 'text-sm' : 'text-base'}`}
            >
              {recipe.title}
            </Link>
            <Badge variant="outline" className={`shrink-0 text-xs ${getScoreColor(score)}`}>
              <Star className="h-3 w-3 mr-1" />
              {getScoreLabel(score)}
            </Badge>
          </div>

          {/* Recipe details */}
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            {recipe.prepTime && (
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {recipe.prepTime}
              </div>
            )}
          </div>

          {/* All recipe tags */}
          {recipe.tags && recipe.tags.length > 0 && (
            <div className="flex items-start gap-1 text-xs">
              <Tag className="h-3 w-3 mt-0.5 text-muted-foreground shrink-0" />
              <div className="flex flex-wrap gap-1">
                {recipe.tags.map((tag, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Similarity reasons */}
          {reasons.length > 0 && (
            <div className="text-xs text-muted-foreground">
              <span className="font-medium">Why similar:</span> {reasons.slice(0, 2).join(", ")}
              {reasons.length > 2 && "..."}
            </div>
          )}

          {/* Action button */}
          <Button
            size="sm"
            variant="outline"
            onClick={() => onSelect(recipe)}
            className="w-full mt-2 text-xs"
          >
            Use This Recipe
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}